import {
  EnvironmentProviders,
  InjectionToken,
  Optional,
  makeEnvironmentProviders,
} from '@angular/core';
import type { FirebaseStorage as FbStorage } from 'firebase/storage';
import {
  connectStorageEmulator as rawConnectStorageEmulator,
  deleteObject as rawDeleteObject,
  getBlob as rawGetBlob,
  getBytes as rawGetBytes,
  getDownloadURL as rawGetDownloadURL,
  getMetadata as rawGetMetadata,
  getStorage as rawGetStorage,
  getStream as rawGetStream,
  list as rawList,
  listAll as rawListAll,
  ref as rawRef,
  updateMetadata as rawUpdateMetadata,
  uploadBytes as rawUploadBytes,
  uploadBytesResumable as rawUploadBytesResumable,
  uploadString as rawUploadString,
} from 'firebase/storage';
import { kdvWrap } from '@fido/firebase';
import { FirebaseApp } from '@fido/firebase/app';
import { AppCheck } from '@fido/firebase/app-check';
import { Auth } from '@fido/firebase/auth';
import { fromTask as vendorFromTask, percentage as vendorPercentage } from './lib/vendor';

// 1) Wrapped firebase/storage functions. All use blockUntilFirst=true,
//    matching @angular/fire's _zoneWrap declarations from
//    angular-fire-storage.mjs.
export const connectStorageEmulator = kdvWrap(rawConnectStorageEmulator, true);
export const deleteObject = kdvWrap(rawDeleteObject, true);
export const getBlob = kdvWrap(rawGetBlob, true);
export const getBytes = kdvWrap(rawGetBytes, true);
export const getDownloadURL = kdvWrap(rawGetDownloadURL, true);
export const getMetadata = kdvWrap(rawGetMetadata, true);
export const getStorage = kdvWrap(rawGetStorage, true);
export const getStream = kdvWrap(rawGetStream, true);
export const list = kdvWrap(rawList, true);
export const listAll = kdvWrap(rawListAll, true);
export const ref = kdvWrap(rawRef, true);
export const updateMetadata = kdvWrap(rawUpdateMetadata, true);
export const uploadBytes = kdvWrap(rawUploadBytes, true);
export const uploadBytesResumable = kdvWrap(rawUploadBytesResumable, true);
export const uploadString = kdvWrap(rawUploadString, true);

export type {
  StorageReference,
  UploadTask,
  UploadTaskSnapshot,
  UploadResult,
  UploadMetadata,
  FullMetadata,
  ListResult,
  SettableMetadata,
  StringFormat,
} from 'firebase/storage';

// 2) DI token + provider.
export type Storage = FbStorage;
export const Storage = new InjectionToken<FbStorage>('@fido/firebase.storage');

export function provideStorage(factory: () => FbStorage): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: Storage,
      // FirebaseApp + (Optional) Auth + (Optional) AppCheck — mirrors
      // @angular/fire's provideStorage multi-instance deps. Comment from
      // upstream: "Defensively load Auth first, if provided." Storage rules
      // typically gate writes on auth state, so loading Auth first avoids
      // permission-denied races on cold-load uploads. AppCheck dep gives the
      // token mint a head start.
      useFactory: (_app: unknown, _auth: unknown, _appCheck: unknown) => factory(),
      deps: [FirebaseApp, [new Optional(), Auth], [new Optional(), AppCheck]],
    },
  ]);
}

// 3) Vendored rxfire helpers — Observable-shaped upload-progress streams.
//    Each is wrapped in kdvWrap for SSR pending-task tracking. Identical
//    surface to @angular/fire/storage.
export const fromTask = kdvWrap(vendorFromTask, true);
export const percentage = kdvWrap(vendorPercentage, true);
